import { useState } from 'react';
import { X, CheckCircle2, Target, Plug, TrendingUp, ShieldCheck, ArrowRight, Sparkles } from 'lucide-react';

/**
 * AppOnboardingSetup — pas in /app na aankoop/pilot-redeem.
 *
 * P0/P5 Flow-fix (Feb 2026):
 * De setupvragen mogen niet meer in de pre-sale flow staan. Deze sheet
 * verschijnt pas binnen Mijn Powersure (`/app`) na app-opening en bestaat
 * uit 4 korte vragen:
 *   1. Wat wilt u bereiken?
 *   2. Welke apparaten heeft u?
 *   3. Heeft u een P1-meter / slimme meter?
 *   4. Wilt u eerst alleen inzicht of later ook sturing?
 *
 * Antwoorden worden lokaal opgeslagen in localStorage (geen PII naar cloud).
 */

const STORAGE_KEY = 'powersure_app_setup_v1';

export function readSetup() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function isSetupCompleted() {
  const s = readSetup();
  return !!(s && s.completed_at);
}

const GOALS = [
  { id: 'inzicht', label: 'Vooral inzicht krijgen' },
  { id: 'besparen', label: 'Minder stroom van het net' },
  { id: 'terugleveren_minder', label: 'Minder terugleveren' },
  { id: 'apparaten_koppelen', label: 'Apparaten slim koppelen' },
];
const DEVICES = [
  { id: 'zonnepanelen', label: 'Zonnepanelen' },
  { id: 'omvormer', label: 'Omvormer' },
  { id: 'laadpaal', label: 'Laadpaal' },
  { id: 'warmtepomp', label: 'Warmtepomp' },
  { id: 'boiler', label: 'Boiler' },
  { id: 'batterij', label: 'Thuisbatterij' },
  { id: 'stekker', label: 'Slimme stekkers' },
];
const P1_OPTIONS = [
  { id: 'ja', label: 'Ja, ik heb een P1-meter of slimme meter' },
  { id: 'nee', label: 'Nog niet — wil ik later koppelen' },
  { id: 'weet_niet', label: 'Weet ik niet' },
];
const CONTROL_OPTIONS = [
  { id: 'alleen_inzicht', label: 'Eerst alleen inzicht' },
  { id: 'later_sturing', label: 'Later ook sturing (met mijn OK)' },
];

function StepHeader({ icon: Icon, step, total, title }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">
          Stap {step} van {total}
        </span>
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-white mt-2" style={{ fontFamily: 'Outfit' }}>
        {title}
      </h3>
    </div>
  );
}

function ChipButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
        active
          ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/50 font-semibold'
          : 'bg-white/5 text-white/85 border-white/15 hover:bg-white/10 hover:border-emerald-400/30'
      }`}
    >
      {children}
    </button>
  );
}

export default function AppOnboardingSetup({ pilotName, onClose, onCompleted }) {
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState('');
  const [devices, setDevices] = useState([]);
  const [hasP1, setHasP1] = useState('');
  const [control, setControl] = useState('');
  const TOTAL = 4;

  const toggleDevice = (id) => {
    setDevices((cur) => cur.includes(id) ? cur.filter((d) => d !== id) : [...cur, id]);
  };

  const canNext = (() => {
    if (step === 1) return !!goal;
    if (step === 2) return devices.length > 0;
    if (step === 3) return !!hasP1;
    if (step === 4) return !!control;
    return false;
  })();

  const finish = () => {
    const payload = {
      goal,
      devices,
      has_p1: hasP1,
      control_pref: control,
      completed_at: new Date().toISOString(),
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
    if (typeof window !== 'undefined' && window.pwTrack) {
      try { window.pwTrack('app_onboarding_completed'); } catch { /* ignore */ }
    }
    if (onCompleted) onCompleted(payload);
    if (onClose) onClose();
  };

  const skip = () => {
    if (typeof window !== 'undefined' && window.pwTrack) {
      try { window.pwTrack('app_onboarding_skipped'); } catch { /* ignore */ }
    }
    if (onClose) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm"
      data-testid="app-onboarding-overlay"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-emerald-400/30 shadow-2xl p-5 sm:p-6 relative overflow-hidden"
        style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.2) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
        data-testid="app-onboarding-sheet"
      >
        <button
          type="button"
          onClick={skip}
          aria-label="Setup overslaan"
          data-testid="app-onboarding-close"
          className="absolute top-3 right-3 text-white/55 hover:text-emerald-300"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-4">
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">Welkom {pilotName ? pilotName.split(' ')[0] : ''}</div>
          <h2 className="text-xl sm:text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
            Stel Mijn Powersure in
          </h2>
          <p className="text-xs sm:text-sm text-white/65 mt-1.5">
            Vier korte vragen. U kunt dit later altijd aanpassen.
          </p>
        </div>

        <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden mb-5">
          <div
            className="h-1 bg-emerald-400 transition-all duration-300"
            style={{ width: `${(step / TOTAL) * 100}%` }}
            data-testid="app-onboarding-progress"
          />
        </div>

        {step === 1 && (
          <div data-testid="app-onboarding-step-1">
            <StepHeader icon={Target} step={1} total={TOTAL} title="Wat wilt u bereiken?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {GOALS.map((g) => (
                <ChipButton key={g.id} active={goal === g.id} onClick={() => setGoal(g.id)} testId={`app-onb-goal-${g.id}`}>
                  {g.label}
                </ChipButton>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="app-onboarding-step-2">
            <StepHeader icon={Plug} step={2} total={TOTAL} title="Welke apparaten heeft u?" />
            <p className="text-xs text-white/55 mb-2">Meerdere mogelijk. U kunt deze later wijzigen.</p>
            <div className="grid grid-cols-2 gap-2">
              {DEVICES.map((d) => (
                <ChipButton key={d.id} active={devices.includes(d.id)} onClick={() => toggleDevice(d.id)} testId={`app-onb-device-${d.id}`}>
                  {d.label}
                </ChipButton>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="app-onboarding-step-3">
            <StepHeader icon={TrendingUp} step={3} total={TOTAL} title="Heeft u een P1-meter of slimme meter?" />
            <p className="text-xs text-white/55 mb-2">Voor live inzicht koppelt u een meetbron. Zonder kan ook — voor voorbeeld-overzichten.</p>
            <div className="grid grid-cols-1 gap-2">
              {P1_OPTIONS.map((o) => (
                <ChipButton key={o.id} active={hasP1 === o.id} onClick={() => setHasP1(o.id)} testId={`app-onb-p1-${o.id}`}>
                  {o.label}
                </ChipButton>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="app-onboarding-step-4">
            <StepHeader icon={ShieldCheck} step={4} total={TOTAL} title="Eerst alleen inzicht of later ook sturing?" />
            <p className="text-xs text-white/55 mb-2">Automatische sturing staat standaard uit. Pas met uw aparte goedkeuring.</p>
            <div className="grid grid-cols-1 gap-2">
              {CONTROL_OPTIONS.map((o) => (
                <ChipButton key={o.id} active={control === o.id} onClick={() => setControl(o.id)} testId={`app-onb-control-${o.id}`}>
                  {o.label}
                </ChipButton>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 mt-6">
          <button
            type="button"
            onClick={() => (step > 1 ? setStep(step - 1) : skip())}
            className="text-xs px-3 py-2 text-white/55 hover:text-white/85"
            data-testid="app-onboarding-back"
          >
            {step > 1 ? '← Terug' : 'Overslaan'}
          </button>
          {step < TOTAL ? (
            <button
              type="button"
              disabled={!canNext}
              onClick={() => setStep(step + 1)}
              data-testid="app-onboarding-next"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-900/40"
            >
              Volgende <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="button"
              disabled={!canNext}
              onClick={finish}
              data-testid="app-onboarding-finish"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-900/40"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Setup voltooien
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * AppOnboardingBanner — vrijblijvende banner bovenaan dashboard
 * wanneer setup nog niet voltooid is. Klik opent de sheet.
 */
export function AppOnboardingBanner({ onOpen, testId = 'app-onboarding-banner' }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={testId}
      className="w-full text-left rounded-2xl border border-emerald-400/30 p-4 flex items-center gap-3 hover:border-emerald-400/60 transition-colors"
      style={{ background: 'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.15) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
    >
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 shrink-0">
        <Sparkles className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>Stel Mijn Powersure in</div>
        <div className="text-xs text-white/65 leading-snug mt-0.5">
          Vier korte vragen — uw doel, apparaten, meetbron en sturing-voorkeur.
        </div>
      </div>
      <ArrowRight className="w-4 h-4 text-emerald-300 shrink-0" />
    </button>
  );
}
